import classNames from 'classnames';
import Text from 'components/ui/Text';
import { useEffect, useState } from 'react';
import { RecipeValueFactory } from 'factories/recipeValueFactory';
import { selectRoomState, selectSelectedRoomOptions } from 'store/roomSlice';
import { useAppSelector } from 'store/hooks';
import { RecipeGraphValues } from 'type';
import { useTranslation } from 'hooks/useTranslation';
import Graph from 'components/Graph';
import { getTooltip } from 'utils/tooltip';
import { selectGeneralState } from 'store/generalSlice';
import Tooltip from 'components/Tooltip';
import RichText from 'components/ui/RichText';
import Icons from 'components/icons';
import LightingKnowledgeLink from 'components/LightingKnowledgeLink';

const RecipeGraph = ({
  large = false,
  summary = false,
}: {
  large?: boolean;
  summary?: boolean;
}) => {
  const currentRoom = useAppSelector(selectRoomState);
  const translation = useTranslation();
  const currentRoomOptions = useAppSelector(selectSelectedRoomOptions);
  const [recipeGraphValues, setRecipeGraphValues] =
    useState<RecipeGraphValues | null>(null);

  const { tooltips } = useAppSelector(selectGeneralState);

  useEffect(() => {
    const combinedValues = RecipeValueFactory.combineValues(currentRoomOptions);
    const normalizedValues = RecipeValueFactory.normalizeValues(
      currentRoom,
      combinedValues,
    );
    const graphValues =
      RecipeValueFactory.convertToRecipeGraphValues(normalizedValues);
    setRecipeGraphValues(graphValues);
  }, [currentRoomOptions, currentRoom]);

  const graphAreaClasses = classNames('w-full');

  // const tooltip = {
  //   sustainability: getTooltip(
  //     translation('recipe.categories.sustainability'),
  //     tooltips,
  //   ),
  //   human: getTooltip(translation('recipe.categories.human'), tooltips),
  //   financial: getTooltip(translation('recipe.categories.financial'), tooltips),
  // };

  const CategoryTitle = ({
    title,
    className,
  }: {
    title: string;
    className?: string;
  }) => {
    const tooltip = getTooltip(title, tooltips);

    return (
      <Text
        bold
        color="grey-800"
        elementType="div"
        size="large"
        className={classNames(
          'flex items-center justify-start gap-2 relative',
          className,
        )}
      >
        {title}

        {tooltip && (
          <Tooltip
            content={
              <div className="text-sm tooltip">
                <RichText text={tooltip.text || []} />

                {tooltip.solutionId && (
                  <LightingKnowledgeLink
                    id={tooltip.solutionId}
                    sectionId={tooltip.sectionId}
                  >
                    {translation('general.learnMore')}
                  </LightingKnowledgeLink>
                )}
              </div>
            }
          >
            <Icons.Info className="!h-5 fill-blue" />
          </Tooltip>
        )}
      </Text>
    );
  };

  // const getSummaryValue = (values:number[], aim:number)=>values.find((value)=>value >= aim)

  const findLargestNumber = (numbers: number[]): number | null =>
    numbers.length ? Math.max(...numbers) : null;

  return (
    <>
      {recipeGraphValues && (
        <>
          <div className={graphAreaClasses}>
            <CategoryTitle
              title={translation('recipe.categories.sustainability')}
            />

            <Graph
              large={large}
              summary={
                summary
                  ? findLargestNumber([recipeGraphValues.co2.color])
                  : undefined
              }
              bars={[
                {
                  category: currentRoomOptions?.lightingSolution?.category,
                  large,
                  colorType: 'sustainability',
                  title: translation('recipe.values.co2'),
                  color: recipeGraphValues.co2.color,
                  height: recipeGraphValues.co2.height,
                },
              ]}
            />
          </div>

          <div className={graphAreaClasses}>
            <CategoryTitle title={translation('recipe.categories.human')} />
            <Graph
              summary={
                summary
                  ? findLargestNumber([
                      recipeGraphValues.biological.color,
                      recipeGraphValues.emotional.color,
                    ])
                  : undefined
              }
              large={large}
              bars={[
                {
                  category: currentRoomOptions?.lightingSolution?.category,
                  large,
                  colorType: 'human',
                  title: translation('recipe.values.visual'),
                  color: recipeGraphValues.visual.color,
                  height: recipeGraphValues.visual.height,
                },
                {
                  category: currentRoomOptions?.lightingSolution?.category,
                  large,
                  colorType: 'human',
                  title: translation('recipe.values.biological'),
                  color: recipeGraphValues.biological.color,
                  height: recipeGraphValues.biological.height,
                },
                {
                  category: currentRoomOptions?.lightingSolution?.category,
                  large,
                  colorType: 'human',
                  title: translation('recipe.values.emotional'),
                  color: recipeGraphValues.emotional.color,
                  height: recipeGraphValues.emotional.height,
                },
              ]}
            />
          </div>

          <div className={graphAreaClasses}>
            <CategoryTitle
              title={translation('recipe.categories.financial')}
              className="mb-0"
            />

            <Graph
              summary={
                summary
                  ? findLargestNumber([recipeGraphValues.energyCost.color])
                  : undefined
              }
              large={large}
              bars={[
                {
                  category: currentRoomOptions?.lightingSolution?.category,
                  large,
                  colorType: 'financial',
                  title: translation('recipe.values.luminaireCost'),
                  color: recipeGraphValues.luminaireCost.color,
                  height: recipeGraphValues.luminaireCost.height,
                },
                {
                  category: currentRoomOptions?.lightingSolution?.category,
                  large,
                  colorType: 'financial',
                  title: translation('recipe.values.energyCost'),
                  color: recipeGraphValues.energyCost.color,
                  height: recipeGraphValues.energyCost.height,
                },
              ]}
            />
          </div>
        </>
      )}
    </>
  );
};

export default RecipeGraph;
